/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { HeroImageCombined, FlexRow, FlexCol, withLDConsumer } from 'sarsaparilla';
import { isDailyPassType } from 'ui-sitepass/src/utils/date_time_utils';
import { today } from '@internationalized/date';
import { formatDateShortWithWeekDay } from 'ui-activitypass/src/utils/upsell_utils';
import { isValidDateFormat, parseDate } from 'ui-activitypass/src/utils/date_time_utils';
import { FacilityProps, PassTypeProps, ReservationProps } from '../props';
import { formatPrice, getSeasonDates, getUnitCardinality } from '../utils';
import { getDuration, validToDate } from '../utils/duration_utils';
import CostsTable from './CostsTable';

export class ReservationSummary extends Component {
    static propTypes = {
        children: PropTypes.node,
        facility: FacilityProps,
        passTypes: PropTypes.arrayOf(PassTypeProps),
        reservations: PropTypes.arrayOf(ReservationProps),
        flags: PropTypes.object,
    };

    static total = '$0.00';

    static buildCosts(passType, reservation) {
        const daySegmentGroups = get(passType, ['features', 'day-segment', 'groups'], {});
        const daySegmentAttendees = get(
            reservation,
            ['features', 'day-segment', 'groups'],
            {}
        );
        const groups = get(passType, ['features', 'attendee-info'], daySegmentGroups);
        const attendees = get(
            reservation,
            ['features', 'attendee-info'],
            daySegmentAttendees
        );
        const attendeeInfo = [];
        const costs = [
            { title: `${passType.name}`, quantity: 'x 1', value: reservation.price },
        ];
        if (reservation.fees) {
            costs.push({
                title: `Service Fee`,
                quantity: 'x 1',
                value: reservation.fees,
            });
        }
        const total = { title: 'Subtotal', value: reservation.total };
        let allowedToBuy = true;

        if (Object.keys(attendees).length) {
            Object.entries(groups).forEach(([key, group]) => {
                const count = attendees[key];

                if (!count) {
                    if (group.attendee_min > 0) {
                        allowedToBuy = false;
                    }
                    return;
                }

                if (group.attendee_max > -1) {
                    if (group.price_per_attendee && count > group.attendee_max) {
                        allowedToBuy = false;
                    }
                }

                attendeeInfo.push(
                    <span
                        key={key}
                        className="activitypass-reservation-summary-attendee-info"
                    >
                        {`${count} ${getUnitCardinality(count, key.replace(/s$|ren$/, ''), key)} `}
                    </span>
                );

                let cost;

                cost = Math.max(group.price_min, count * group.price_per_attendee);
                if (group.price_max > 0) {
                    cost = Math.min(group.price_max, cost);
                }

                if (cost) {
                    costs.push({
                        title: passType.name,
                        quantity: `x 1`,
                        description: passType.description,
                        value: cost,
                    });

                    total.value += cost;
                }
            });
        }

        return { allowedToBuy, attendeeInfo, costs, total };
    }

    shouldComponentUpdate(nextProps) {
        return nextProps.reservations.every(
            (res) => res.has_price || isDailyPassType(res?.pass_info?.duration)
        );
    }

    render() {
        const { facility, passTypes, reservations, children } = this.props;
        const totalCost = { title: 'Total Cost', value: 0 };
        let hasErrorInPass = false;

        return (
            <div className="aside activitypass-reservation-summary">
                <div className="activitypass-reservation-image-wrap">
                    <HeroImageCombined
                        id={facility.asset_id || facility.id}
                        type="asset"
                    />
                </div>

                <div className="activitypass-reservation-summary-body-wrap">
                    {!reservations.length && (
                        <FlexRow
                            hasGutters={false}
                            className="activitypass-reservation-summary-subhead-wrap"
                        >
                            <FlexCol>
                                <div
                                    className="activitypass-reservation-summary-title"
                                    translate="no"
                                >
                                    {facility.name}
                                </div>
                            </FlexCol>
                        </FlexRow>
                    )}

                    {reservations.map((reservation, number) => {
                        const passType = passTypes.find(
                            ({ id }) => id === reservation.pass_type_id
                        );
                        if (!passType) {
                            return null;
                        }
                        const { allowedToBuy, attendeeInfo, costs, total } =
                            ReservationSummary.buildCosts(passType, reservation);
                        totalCost.value += total.value;
                        ReservationSummary.total = formatPrice(totalCost.value);

                        const isSeasonal = ['season', 'seasonal'].includes(
                            passType.duration
                        );

                        const [seasonStart, seasonEnd] = getSeasonDates(
                            passType.periods,
                            undefined,
                            this.props?.flags?.useApDateFix
                        );
                        let seasonEndCalendar = isValidDateFormat(seasonEnd)
                            ? parseDate(seasonEnd)
                            : undefined;

                        const startDateFeature =
                            reservation?.pass_info?.features['start-date'] ||
                            today().toString();

                        const startDateCalendar = parseDate(
                            isSeasonal ? seasonStart : startDateFeature
                        );

                        // Some durations are flexible/user-customizable, so we have to calculate them instead of using durations.
                        const duration = getDuration(passType.duration);
                        const canModifyEndDate =
                            passType.features['end-date'] ||
                            passType.features['day-segment'] ||
                            passType.duration === 'segment';
                        if (canModifyEndDate) {
                            const end = reservation.pass_info.features['end-date'];
                            const endDatePlus2Days = parseDate(end).add({ days: 2 });
                            duration.days = Math.abs(endDatePlus2Days.compare(today()));
                        } else if (isSeasonal) {
                            // For seasonal pricing, we have to get the end date ourselves.
                            const seasonEndDatePlus2Days = parseDate(seasonEnd).add({
                                days: 2,
                            });
                            duration.days = Math.abs(
                                seasonEndDatePlus2Days.compare(today())
                            );
                        }

                        if (!startDateCalendar) {
                            const [year, month, day] = seasonEnd?.split('-') ?? [
                                undefined,
                                undefined,
                                undefined,
                            ];
                            const adjustedYear = parseInt(year, 10) + 1;
                            seasonEndCalendar = parseDate(
                                `${adjustedYear}-${month}-${day}`
                            );
                        }

                        let startDate = formatDateShortWithWeekDay(startDateCalendar);
                        let endDate = formatDateShortWithWeekDay(
                            isSeasonal
                                ? seasonEndCalendar
                                : validToDate(startDateCalendar, passType)
                        );

                        const isEmptyDailyPassDate =
                            isDailyPassType(passType?.duration) &&
                            isEmpty(reservation?.pass_info?.features['start-date']);

                        if (isEmptyDailyPassDate) {
                            startDate = 'Select Start date';
                            endDate = 'Select Start date';
                        }

                        hasErrorInPass = hasErrorInPass || !allowedToBuy;

                        return (
                            <div
                                key={reservation.id}
                                className="activitypass-reservation-summary-item"
                            >
                                <FlexRow
                                    hasGutters={false}
                                    className="activitypass-reservation-summary-subhead-wrap"
                                >
                                    <FlexCol>
                                        <div className="activitypass-reservation-summary-title">
                                            {number + 1}. {passType.name}
                                        </div>
                                        <div className="activitypass-reservation-summary-name">
                                            {facility.name}
                                        </div>
                                    </FlexCol>
                                </FlexRow>

                                <FlexRow
                                    hasGutters={false}
                                    className="activitypass-reservation-summary-item-body"
                                >
                                    <FlexCol
                                        xs={6}
                                        md={12}
                                        xl={6}
                                        className="activitypass-reservation-summary-dates"
                                    >
                                        <FlexRow xs={12}>
                                            <FlexCol>Start Date</FlexCol>
                                            <FlexCol xs={12}>
                                                <strong>{startDate}</strong>
                                            </FlexCol>
                                        </FlexRow>
                                    </FlexCol>

                                    <FlexCol xs={6} md={12} xl={6}>
                                        <FlexRow>
                                            <FlexCol xs={12}>Valid Through</FlexCol>
                                            <FlexCol xs={12}>
                                                <strong>{endDate}</strong>
                                            </FlexCol>
                                        </FlexRow>
                                    </FlexCol>
                                </FlexRow>

                                {allowedToBuy ? (
                                    <div>
                                        <CostsTable
                                            lines={costs}
                                            total={total}
                                            attendeeInfo={attendeeInfo}
                                        />
                                    </div>
                                ) : (
                                    <strong className="rec-text-danger">
                                        Your selections are outside the bounds for this
                                        pass type. Please make a different selection.
                                    </strong>
                                )}
                            </div>
                        );
                    })}

                    <CostsTable
                        lines={[]}
                        total={totalCost}
                        showNoLinesText={!reservations.length}
                    />

                    {!hasErrorInPass && children}
                </div>
            </div>
        );
    }
}

// cspell:ignore activitypass
export default withLDConsumer()(ReservationSummary);
